<template>
    <div>
        <h3>{{slide.slide.title}}</h3>
        <div v-if="slide.slide.corpus">
            <div style="text-align: left" v-html="sanitize(slide.slide.corpus)"></div>
            <hr>
        </div>
        <h2 style="margin: 30px 0;">
            <span v-if="!complete">
                Which tests would you like to run at this time?
            </span>
            <span v-else>
                Review Necessary Tests
            </span>
        </h2>
        <i v-if="!complete">
            Note: you will not be able to pick more tests after clicking submit.
        </i>
        <i v-if="complete">
            - Tests highlighted green are necessary tests you purchased, tests in red are those you should have purchased. 
            <br>
            - If left uncolored, the test was not deemed necessary, but could still be helpful in solving this case.
        </i>
        <hr>
        <div v-if="slideChangedSinceAttempt" class="question-review-change-notice">
            <b>NOTICE: Section has changed since this attempt!</b>
            <br>
        </div>
        <br>
        <center>
            <md-field md-clearable class="md-toolbar-section-end" style="max-width: 450px;">
                <md-input placeholder="Search lab panels..." v-model="panelSearchTerms" @input="panelSearch"/>
            </md-field>
            <md-switch v-model="showOnlyGradedLabs" v-if="complete">Show Only High/Low Value Labs</md-switch>
        </center>
        <div :class="{'vertical-layout': useVerticalLayout, 'lab-list-container': true}"> 
            <div v-for="(panel, panelIndex) in panelSearchResults.filter((panel) => !showOnlyGradedLabs || panel.should_order || panel.should_not_order)" 
            :key="panel.id" class="panel-card md-elevation-4" style="background-color: #f6f6f6;">
                <PanelViewer :panel="panel" :panelIndex="panelIndex" :complete="complete" :useVerticalLayout="useVerticalLayout"
                :panelPurchased="purchasedPanels.includes(panel.id)" @purchasePanel="purchasePanel"/>
            </div>
        </div>
        <hr>
        <h3>
            Spent ${{this.totalCost()}} of ${{this.maxCost()}}
            <br>
            <span v-if="slide.complete">
                <br>
                Labs Score: {{this.calcScore().toFixed(0)}}%
            </span>
        </h3>
        <i>
            Note: Gain points by purchasing necessary labs, lose points by purchasing low value labs.
            <br>
            Scroll back up to review how labs were graded.
        </i>
        <br>
        <br>
        <md-button class="md-raised md-primary" v-if="!complete" @click="submitAnswer()">
            Submit
        </md-button>
        <md-button class="md-raised md-primary" v-if="complete" @click="$emit('setDone', [slide.slide.order, true])">
            Continue
        </md-button>
    </div>
</template>

<script>
    import Fuse from "fuse.js"
    import hasher from "object-hash"
    import PanelViewer from "./PanelViewer.vue"

    export default {
        props: {
            useVerticalLayout: {
                type: Boolean
            },
            slide: {
                type: Object
            }, 
            attemptRecord: {
                type: Object
            },
            complete: {
                type: Boolean
            },
            sanitize: {
                type: Function
            },
        },
        data: function(){
            return {
                panelSearchTerms: "",
                searchablePanels: undefined,
                panelSearchResults: [],
                panelSearchDebounce: undefined,
                showOnlyGradedLabs: false,
                purchasedPanels: [],
                slideChangedSinceAttempt: false,
                purchasedPanelData: {
                    ids: [],
                    required: [],
                    costs: [],
                    totalCost: 0,
                    maxCost: 0,
                    missedPanels: {
                        names: [],
                        costs: [],
                    }
                }
            }
        },
        components: {
            PanelViewer
        },
        methods: {
            panelSearch: function(){
                clearTimeout(this.panelSearchDebounce)
                if(!this.searchablePanels){
                    this.searchablePanels = Object.values(this.slide.panels).sort((x, y) => x.name.localeCompare(y.name))
                }
                if(this.panelSearchTerms.trim() == ""){
                    this.panelSearchResults = this.searchablePanels
                    return
                }
                this.panelSearchDebounce = setTimeout(() => {
                    let fuseOptions = {
                        caseSensitive: false,
                        tokenize: true,
                        shouldSort: true,
                        threshold: 0.2,
                        keys: ['name'],
                    }
                    var fuse = new Fuse(this.searchablePanels, fuseOptions)
                    this.panelSearchResults =  fuse.search(this.panelSearchTerms)
                }, 500)
            },
            purchasePanel: function([testID, required, cost]){
                if(this.complete) return
                if (this.purchasedPanels.indexOf(testID) === -1) {
                    this.purchasedPanels.push(testID);
                    this.purchasedPanelData.ids.push(testID);
                    this.purchasedPanelData.required.push(required);
                    this.purchasedPanelData.costs.push(cost);
                    this.purchasedPanelData.totalCost = this.totalCost();
                }
                this.$emit("notify", "Total = $" + this.purchasedPanelData.totalCost)
            },
            totalCost: function(){
                var totalCost = 0;
                for(var i=0; i < this.slide.panels.length; i++){
                    if(this.purchasedPanels.includes(this.slide.panels[i].id)){
                        if(this.slide.panels[i].cost) totalCost += this.slide.panels[i].cost
                    }
                }
                return parseFloat(totalCost.toFixed(2))
            },
            maxCost: function(){
                var maxCost = this.slide.panels.reduce((maxCost, entry) => {
                    return maxCost + entry.cost
                }, 0)
                return parseFloat(maxCost.toFixed(2))
            },
            calcScore: function(){
                let totalRequiredLabs = 0;
                let missedLabs = 0;
                let lowValueLabsPurchased = 0;
                this.slide.panels.forEach((panel) => {
                    let purchased = this.purchasedPanels.includes(panel.id) 
                    if(panel.should_order){
                        totalRequiredLabs++
                        if (!purchased) {
                            missedLabs++
                        }
                    }
                    if(panel.should_not_order && purchased){
                        lowValueLabsPurchased++
                    }
                })
                return Math.max(0, totalRequiredLabs > 0 ? (100 * (totalRequiredLabs - missedLabs - lowValueLabsPurchased) / totalRequiredLabs) : 100)
            },
            submitAnswer: function(){
                this.slide.complete = true
                let performance = {
                    performance: {
                        "QHash": hasher(JSON.stringify(this.slide))
                    },
                }
                this.slide.panels.forEach((panel) => {
                    if(panel.should_order){
                        if (this.purchasedPanels.indexOf(panel.id) === -1) {
                            this.purchasedPanelData.missedPanels.names.push(panel.name)
                            this.purchasedPanelData.missedPanels.costs.push(panel.cost)
                        }
                    }
                })
                performance.score = this.calcScore()
                this.purchasedPanelData.maxCost = this.maxCost()
                performance.performance[this.slide.type] = this.purchasedPanelData
                this.$emit('setDone', [this.slide.slide.order, false, performance]);
            },
            loadAttemptAnswer: function(){
                if(this.attemptRecord && this.attemptRecord.performance && Object.keys(this.attemptRecord.performance).length != 0){
                    if(this.attemptRecord.performance["Labs"] && this.attemptRecord.performance["Labs"].ids){
                        this.purchasedPanels = this.attemptRecord.performance["Labs"].ids
                    }
                    this.slideChangedSinceAttempt = hasher(JSON.stringify(this.slide)) != this.attemptRecord.performance["QHash"]
                }
            }
        },
        watch: {
            "attemptRecord": function() {
                this.loadAttemptAnswer()
            }
        },
        mounted: function(){
            this.$nextTick(function () {
                this.panelSearch()
                this.loadAttemptAnswer()
            })
        }

    }
</script>

<style scoped>
    .panel-card{
        padding: 20px;
        margin: 50px 0;
        border-radius: 5px !important;
    }
    .lab-list-container{
        background-color: #ffffffaa;
    }
    .lab-list-container:not(.vertical-layout){
        max-height: 80vh; 
        overflow-y: auto; 
        padding: 0 20px; 
        border: 1px solid #ddd; 
        border-radius: 8px;
    }
</style>